import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { WidgetCore } from '../../interfaces/widget.core.interface';
import { AnalyticsService } from '../../../analytics/services/analytics.service';
import { EVENT_EMITTERS, MAP_CONSTANTS } from '../../../analytics/constants/analytics.constants';
import * as _ from 'underscore';
import { EmitterService } from '../../../../core/utility/services/emitter.service';
import { ColorUtilityService } from '../../../analytics-core/services/color.utility.service';
import { AnalyticsDataModel } from '../../../analytics/models/analytics.datamodel';
import * as numeral from 'numeral';

@Component({
	selector: 'app-overview-r3-stats',
	templateUrl: './overview-r3-stats.component.html',
	styleUrls: ['./overview-r3-stats.component.css']
})
export class OverviewR3StatsComponent implements OnInit, WidgetCore, OnDestroy {
	@Input() key: string;
	@Input() uniqueoverwriteid: string;
	@Output() selectedLegend = new EventEmitter<any>();

	public overviewStats: any[] = [];
	public mapObj;
	private colors = 'primary,secondary,tertiary,quaternary,quinary';
	public partnerColors: string[] = [];
	public initiateMapStatsSubscription;
	public widgetDataSubscription;
	public showLiftData = false;
	public offlineDataAccess = false;
	public isMarketronPartner = false;
	public youtubeViewsPermission: boolean = false;
	public youtubeViewsPermissionCheck: object = { permission: { flags: ['hasYoutubeViews'] } };
	public tooltipOptions = {
		position: 'top',
		animation: 'fade',
		arrowSize: 'small',
		distance: 0,
		offset: 0,
		arrow: true,
		size: 'small',
		duration: 750,
		scale: 'small',
		dynamicTitle: true,
		theme: 'overview-quick-stats'
	};

	constructor(
		private analyticsService: AnalyticsService,
		private colorUtilityService: ColorUtilityService,
		private analyticsDataModel: AnalyticsDataModel
	) {}

	ngOnInit() {
		this.partnerColors = this.colors.split(',');
		this.isMarketronPartner = this.analyticsDataModel.config.partner.isMarketron;
		this.youtubeViewsPermission = this.analyticsDataModel.checkComponentForPermission(this.youtubeViewsPermissionCheck);
		this.initiateMapStatsSubscription = EmitterService.get(EVENT_EMITTERS.INITIATE_MAP_STATS).subscribe(
			(response: any) => {
				this.mapObj = response;
				setTimeout(() => {
					this.requestData();
				}, 5000); //TODO: remove timeout and figure out why selected advertisers and campaigns are not getting populated by call time.
			}
		);
	}

	ngOnDestroy() {
		if (this.initiateMapStatsSubscription) {
			this.initiateMapStatsSubscription.unsubscribe();
		}
		if (this.widgetDataSubscription) {
			this.widgetDataSubscription.unsubscribe();
		}
	}

	requestData() {
		this.widgetDataSubscription = this.analyticsService
			.processOverviewRequest(this.key, null, this.uniqueoverwriteid)
			.subscribe((response: any) => {
				if (response.success) {
					this.processResponseData(response);
				}
			});
	}

	processResponseData(response: any) {
		const responseData = response.data;
		let transformedOverviewStats = this.mapResponseDataToInterface(responseData);
		let overviewStats = _.map(this.mapObj.legend, (item) => {
			const legend = _.findWhere(transformedOverviewStats, { productKey: item['productKey'] });
			return _.extend(item, legend);
		});
		this.overviewStats = overviewStats.map((overviewstat, index) => {
			overviewstat.sectionClass = this.getClassByIndex(index);
			if (!overviewstat.clicks && !overviewstat.impressions && !overviewstat.ctr) {
				overviewstat.isDisabled = true;
			}
			if (overviewstat.productKey === 'social' || overviewstat.productKey === 'email') {
				overviewstat.isDisabled = true;
			}
			if (overviewstat.mergeFrom) {
				const mergeFrom = overviewstat.mergeFrom;
				const mergeFromObj: any = _.findWhere(transformedOverviewStats, { productKey: mergeFrom.key });
				if (mergeFromObj) {
					overviewstat.impressions = parseInt(overviewstat.impressions, 10) + parseInt(mergeFromObj.impressions, 10);
					overviewstat.clicks = parseInt(overviewstat.clicks, 10) + parseInt(mergeFromObj.clicks, 10);
					overviewstat.ctr = (overviewstat.clicks / overviewstat.impressions) * 100;
				}
			}
			overviewstat.ctr =
				overviewStats && overviewstat.ctr && overviewstat.ctr != null
					? numeral(overviewstat.ctr).format('0,0.00')
					: null;
			overviewstat.completions_rate =
				overviewstat.completions_rate != null ? numeral(overviewstat.completions_rate).format('0,0.00') : null;

			this.showLiftData = this.showLiftDetails(this.overviewStats).length > 0 ? true : false;
			this.selectedLegend.emit({ legendItem: this.overviewStats, showLiftData: this.showLiftData });
			if (overviewstat.productKey === 'ctv') {
				overviewstat.productName = 'CTV / OTT';
			}
			return overviewstat;
		});

		// For Non Marketron partners: moving Offline visits data from diaply card to geofence card on oerview page

		if (this.offlineDataAccess && !this.isMarketronPartner) {
			let displayProduct = this.overviewStats.filter((product) => {
				if (product.productKey == 'display') {
					return product;
				}
			});
			this.overviewStats.forEach((product) => {
				if (product.productKey == 'geofence') {
					product.offline_visits = displayProduct[0].offline_visits;
				}
			});
		}
	}

	showLiftDetails(selectedProducts) {
		return _.filter(selectedProducts, (product: any) => {
			return (
				(product.productName === 'Display' && product.productSelected) ||
				(product.productName === 'Pre Roll' && product.productSelected)
			);
		});
	}

	selectedProductDetails(event, item) {
		if (!item.isDisabled) {
			event.stopPropagation();
			this.selectedLegend.emit({ legendItem: item, showLiftData: this.showLiftData });
		}
	}

	mapResponseDataToInterface(responseData) {
		const widget = {
			permission: {
				flags: ['hasOfflineVisits']
			}
		};
		this.offlineDataAccess = this.analyticsDataModel.checkComponentForPermission(widget);
		responseData = responseData.map((response, index) => {
			response.productId = parseInt(response.product_id);
			response.productSelected = true;
			response.productName = response.product_name;
			response.productKey = response.product_name.toLowerCase().split(' ').join('');
			if (response.productName === 'Connected TV') {
				response.productKey = 'ctv';
			} else if (response.productName === 'Streaming Audio') {
				response.productKey = MAP_CONSTANTS.STATE.RADIO;
			} else if (response.productName === 'Video Geofencing') {
				response.productName = 'GeoVideo';
				response.productKey = 'geovideo';
			}
			if (!this.offlineDataAccess) delete response.offline_visits;
			if (!this.youtubeViewsPermission) delete response?.youtube_verified_imprs;
			return response;
		});
		return responseData.filter((item) => item.product_name !== 'Total');
	}

	getClassByIndex(index) {
		const colorIndex = index % this.partnerColors.length;
		return (
			this.partnerColors[colorIndex].trim() + '-color-bg ' + this.partnerColors[colorIndex].trim() + '-color-border'
		);
	}
}
